import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import sessionAuthApi, { type AuthProviders } from "../../services/sessionAuth";
import { startGoogleLogin } from "../../services/googleOAuth";
import AppButton from "../../components/form/AppButton";
import AppTextField from "../../components/form/AppTextField";
import { AuthPageShell } from "../../components/layout";
import authService from "../../services/authService";
import { workspaceService } from "../../services/workspaceService";
import { extractRolesFromUser, needsWorkspaceOnboarding, postLoginPath } from "../../utils/authorization";
import SliderCaptcha from "../../components/SliderCaptcha";
import { authEntryButtonSx } from "./authEntryButtonSx";

function LoginUI() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  useEffect(() => {
    sessionAuthApi.getAuthProviders().then(setProviders).catch(() => setProviders(null));
  }, []);

  // The OAuth callback sends failures back here as ?error=oauth.
  useEffect(() => {
    if (params.get("error") === "oauth") {
      setError("Google sign-in could not be completed. Please try again.");
    }
  }, [params]);

  const onGoogle = () => {
    if (!providers) return;
    startGoogleLogin(providers, invite).catch(() => setError("Google sign-in is unavailable right now."));
  };

  useEffect(() => {
    authService.getUser().then((user) => {
      if (user && invite) {
        navigate(`/invite?token=${encodeURIComponent(invite)}`);
        return;
      }
      if (user) {
        const roles = extractRolesFromUser(user);
        navigate(needsWorkspaceOnboarding(roles) ? "/onboarding/workspace" : postLoginPath(roles));
      }
    });
  }, [navigate, invite]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Check CAPTCHA verification
    if (!captchaVerified) {
      setError(t("auth.captchaRequired", { defaultValue: "Please verify you are human" }));
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      await authService.loginWithPassword(username.trim(), password);
      if (invite) {
        await workspaceService.acceptInvite(invite);
      }
      const user = await authService.getUser();
      const roles = extractRolesFromUser(user);
      navigate(needsWorkspaceOnboarding(roles) ? "/onboarding/workspace" : postLoginPath(roles));
    } catch (err: any) {
      setError(
        err?.code === "AUTH_EMAIL_DOMAIN_NOT_ALLOWED"
          ? t("auth.onprem.domainNotAllowed")
          : err?.message || "Login failed"
      );
      // Reset CAPTCHA on error
      setCaptchaVerified(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaVerify = (verified: boolean) => {
    setCaptchaVerified(verified);
    if (verified) {
      setError(null);
    }
  };

  const handleCaptchaReset = () => {
    setCaptchaVerified(false);
  };

  return (
    <AuthPageShell
      title="Sign in"
      subtitle={t("auth.signIn", { defaultValue: "Sign in to your workspace" })}
      footer={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Link to={invite ? `/register?invite=${encodeURIComponent(invite)}` : "/register"}>
              Create account
            </Link>
            <Link to="/forgot-password">Forgot password</Link>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Link to="/">Product overview</Link>
            <Link to="/how-it-works">How it works</Link>
          </Box>
        </Box>
      }
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <AppTextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
          <AppTextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          
          <SliderCaptcha 
            onVerify={handleCaptchaVerify} 
            onReset={handleCaptchaReset}
          />
          
          <AppButton 
            type="submit" 
            fullWidth 
            variant="contained" 
            sx={{ mt: 1.5, ...authEntryButtonSx }} 
            loading={loading}
            disabled={!captchaVerified}
          >
            Sign in
          </AppButton>
          {providers?.googleEnabled && (
            <>
              <Divider>or</Divider>
              <GoogleSignInButton onClick={onGoogle} sx={authEntryButtonSx} />
            </>
          )}
        </Stack>
      </Box>
    </AuthPageShell>
  );
}

export default LoginUI;
